import { FileReader } from '@/platform'
import { getCachedImageResource } from '@/features/preview/imageResourceCache'

export interface OafFramePicture {
  path: string
  zIndex: number
  x: number
  y: number
  width: number
  height: number
  zoomW: number
  zoomH: number
  rotate: number
  opacity: number
  mirrorType: number
}

export interface OafFrame {
  pictures: OafFramePicture[]
}

export interface OafAction {
  frames: OafFrame[]
}

export interface OafPreviewData {
  width: number
  height: number
  background: OafFramePicture | null
  offsetX: number
  offsetY: number
  actions: OafAction[]
}

export interface OafContainRect {
  x: number
  y: number
  width: number
  height: number
}

export interface OafResolvedLayer {
  source: OafFramePicture
  url: string
  image: HTMLImageElement
  x: number
  y: number
  width: number
  height: number
  opacity: number
  rotate: number
  mirrorX: boolean
  mirrorY: boolean
}

export interface OafResolvedFrame {
  layers: OafResolvedLayer[]
}

export interface OafAnimationPlayer {
  data: OafPreviewData
  frames: OafResolvedFrame[]
  width: number
  height: number
  frameDurationMs: number
}

const OAF_EXTENSIONS = new Set(['oaf', 'oaf2'])
const MAX_OAF_ACTIONS = 256
const MAX_OAF_LAYERS = 512
const MAX_OAF_FRAMES = 2000
const MAX_OAF_PICTURES_PER_FRAME = 512
export const OAF_FRAME_DURATION_MS = 1000 / 30

function getExtension(value: string): string {
  const clean = String(value || '').split('?')[0]!.split('#')[0]!
  const name = clean.split('/').pop() || clean
  return name.includes('.') ? name.split('.').pop()!.toLowerCase() : ''
}

function readBoundedCount(reader: FileReader, fieldName: string, max: number): number {
  const count = reader.readInt()
  if (!Number.isInteger(count) || count < 0 || count > max) {
    throw new Error(`OAF ${fieldName} 数量异常：${count}`)
  }
  return count
}

function readOafPicture(reader: FileReader): OafFramePicture {
  return {
    path: reader.readString(),
    zIndex: reader.readInt(),
    x: reader.readInt(),
    y: reader.readInt(),
    width: reader.readInt(),
    height: reader.readInt(),
    zoomW: reader.readInt(),
    zoomH: reader.readInt(),
    rotate: reader.readInt(),
    opacity: reader.readInt(),
    mirrorType: reader.readInt(),
  }
}

/**
 * 判断资源名或 URL 是否为橙光 OAF 动画文件。
 * @param value 文件名、资源路径或 URL。
 * @returns 是否为 `.oaf/.oaf2`。
 */
export function isOafResource(value: string): boolean {
  return OAF_EXTENSIONS.has(getExtension(value))
}

/**
 * 解析 OAF/OAF2 二进制为编辑器预览模型。
 * @param bytes OAF 文件字节。
 * @returns 可供资源管理器预览的动画数据。
 */
export function parseOafPreviewData(bytes: Uint8Array | ArrayBuffer): OafPreviewData {
  const reader = new FileReader(bytes)
  reader.readMSString()
  const descriptionCount = readBoundedCount(reader, '描述', 1024)
  for (let index = 0; index < descriptionCount; index += 1) {
    reader.readString()
    reader.readString()
  }

  reader.readString()
  const width = Math.max(1, reader.readInt())
  const height = Math.max(1, reader.readInt())
  const backgroundPath = reader.readString()
  const backgroundFields = {
    x: reader.readInt(),
    y: reader.readInt(),
    width: reader.readInt(),
    height: reader.readInt(),
    zoomW: reader.readInt(),
    zoomH: reader.readInt(),
    rotate: reader.readInt(),
    opacity: reader.readInt(),
    mirrorType: reader.readInt(),
  }
  const background: OafFramePicture | null = backgroundPath.trim()
    ? {
      path: backgroundPath,
      zIndex: -1,
      ...backgroundFields,
    }
    : null
  const offsetX = reader.readInt()
  const offsetY = reader.readInt()
  reader.readString()
  reader.readString()

  const actionCount = readBoundedCount(reader, '动作', MAX_OAF_ACTIONS)
  const actions: OafAction[] = []
  for (let actionIndex = 0; actionIndex < actionCount; actionIndex += 1) {
    reader.readString()
    reader.readInt()
    const layerCount = readBoundedCount(reader, '图层', MAX_OAF_LAYERS)
    for (let layerIndex = 0; layerIndex < layerCount; layerIndex += 1) {
      reader.readString()
    }
    reader.readString()
    reader.readString()

    const frameCount = readBoundedCount(reader, '帧', MAX_OAF_FRAMES)
    const frames: OafFrame[] = []
    for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
      const pictureCount = readBoundedCount(reader, '帧图片', MAX_OAF_PICTURES_PER_FRAME)
      const pictures: OafFramePicture[] = []
      for (let pictureIndex = 0; pictureIndex < pictureCount; pictureIndex += 1) {
        const picture = readOafPicture(reader)
        reader.readString()
        reader.readString()
        reader.readString()
        pictures.push(picture)
      }
      frames.push({
        pictures: pictures.sort((left, right) => left.zIndex - right.zIndex),
      })
    }
    actions.push({
      frames,
    })
  }

  return {
    width,
    height,
    background,
    offsetX,
    offsetY,
    actions,
  }
}

/**
 * 读取并解析远端 OAF/OAF2 文件。
 * @param url OAF 文件 URL。
 * @returns OAF 预览数据。
 */
export async function fetchOafPreviewData(url: string): Promise<OafPreviewData> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`OAF 读取失败：HTTP ${response.status}`)
  }
  return parseOafPreviewData(await response.arrayBuffer())
}

/**
 * 收集 OAF 动画引用的图像资源路径，供预加载、诊断或未来事件渲染复用。
 * @param data OAF 预览数据。
 * @param actionIndex 动作索引，默认第一组动作。
 * @returns 去重后的资源路径。
 */
export function collectOafResourcePaths(data: OafPreviewData, actionIndex = 0): string[] {
  const paths = new Set<string>()
  if (data.background?.path.trim()) {
    paths.add(data.background.path)
  }
  for (const frame of data.actions[actionIndex]?.frames ?? []) {
    for (const picture of frame.pictures) {
      if (picture.path.trim()) {
        paths.add(picture.path)
      }
    }
  }
  return [...paths]
}

/**
 * 根据源尺寸和目标窗口计算 OAF 等比例完整显示区域。
 * @param sourceWidth OAF 原始宽度。
 * @param sourceHeight OAF 原始高度。
 * @param targetWidth 预览窗口宽度。
 * @param targetHeight 预览窗口高度。
 * @returns 固定显示矩形。
 */
export function getOafContainRect(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
): OafContainRect | null {
  if (sourceWidth <= 0 || sourceHeight <= 0 || targetWidth <= 0 || targetHeight <= 0) return null
  const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight)
  const width = Math.max(1, Math.floor(sourceWidth * scale))
  const height = Math.max(1, Math.floor(sourceHeight * scale))
  return {
    x: Math.floor((targetWidth - width) / 2),
    y: Math.floor((targetHeight - height) / 2),
    width,
    height,
  }
}

/**
 * 计算 OAF 图层最终绘制属性，资源预览和未来 WebGL 事件预览共用该解释规则。
 * @param layer OAF 帧图层。
 * @param image 已加载的图像资源。
 * @param offsetX OAF 全局 X 偏移。
 * @param offsetY OAF 全局 Y 偏移。
 * @param url 图像 URL。
 * @returns 解析后的可绘制图层。
 */
export function resolveOafLayer(
  layer: OafFramePicture,
  image: HTMLImageElement,
  offsetX: number,
  offsetY: number,
  url: string,
): OafResolvedLayer {
  const baseWidth = layer.width > 0 ? layer.width : image.naturalWidth || image.width
  const baseHeight = layer.height > 0 ? layer.height : image.naturalHeight || image.height
  const zoomW = layer.zoomW > 0 ? layer.zoomW : 100
  const zoomH = layer.zoomH > 0 ? layer.zoomH : 100
  const opacity = layer.opacity > 1 ? layer.opacity / 255 : layer.opacity > 0 ? layer.opacity : 1
  return {
    source: layer,
    url,
    image,
    x: layer.x + offsetX,
    y: layer.y + offsetY,
    width: Math.max(1, Math.round((baseWidth * zoomW) / 100)),
    height: Math.max(1, Math.round((baseHeight * zoomH) / 100)),
    opacity: Math.max(0, Math.min(1, opacity)),
    rotate: layer.rotate,
    mirrorX: layer.mirrorType === 2 || layer.mirrorType === 3,
    mirrorY: layer.mirrorType === 1 || layer.mirrorType === 3,
  }
}

/**
 * 创建 OAF 动画播放器数据，统一完成资源路径解析、图片加载和帧图层解释。
 * @param data OAF 预览数据。
 * @param resolveResourceUrl 把 OAF 内部资源路径转换为浏览器可读 URL 的函数。
 * @param actionIndex 动作索引，默认第一组动作。
 * @returns 可供 2D 或 WebGL 渲染的动画播放器数据。
 */
export async function createOafAnimationPlayer(
  data: OafPreviewData,
  resolveResourceUrl: (path: string) => string,
  actionIndex = 0,
): Promise<OafAnimationPlayer> {
  const frames: OafResolvedFrame[] = []
  for (const frame of data.actions[actionIndex]?.frames ?? []) {
    const layers = data.background ? [data.background, ...frame.pictures] : frame.pictures
    const resolvedLayers: OafResolvedLayer[] = []
    for (const layer of layers) {
      const url = resolveResourceUrl(layer.path)
      if (!url) continue
      try {
        const image = await getCachedImageResource(url)
        resolvedLayers.push(resolveOafLayer(layer, image, data.offsetX, data.offsetY, url))
      } catch {}
    }
    frames.push({
      layers: resolvedLayers,
    })
  }
  return {
    data,
    frames,
    width: data.width,
    height: data.height,
    frameDurationMs: OAF_FRAME_DURATION_MS,
  }
}

/**
 * 根据播放起始时间获取当前帧索引。
 * @param player OAF 动画播放器数据。
 * @param startedAt 播放起始时间。
 * @param now 当前时间。
 * @returns 当前帧索引。
 */
export function getOafFrameIndex(player: OafAnimationPlayer, startedAt: number, now = performance.now()): number {
  const frameCount = player.frames.length
  if (!frameCount) return -1
  return Math.floor((now - startedAt) / player.frameDurationMs) % frameCount
}

/**
 * 把已解析的 OAF 帧图层绘制到 2D 画布。
 * @param ctx 2D 画布上下文。
 * @param frame 当前帧。
 */
export function drawOafFrameToCanvas(ctx: CanvasRenderingContext2D, frame: OafResolvedFrame): void {
  for (const layer of frame.layers) {
    ctx.save()
    ctx.globalAlpha = layer.opacity
    ctx.translate(layer.x + layer.width / 2, layer.y + layer.height / 2)
    if (layer.rotate) {
      ctx.rotate((layer.rotate * Math.PI) / 180)
    }
    ctx.scale(layer.mirrorX ? -1 : 1, layer.mirrorY ? -1 : 1)
    ctx.drawImage(layer.image, -layer.width / 2, -layer.height / 2, layer.width, layer.height)
    ctx.restore()
  }
}
